import { useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'

import { SelectObject } from '@components/SelectObject'
import { SelectString } from '@components/SelectString'
import type { Organisatie } from '@models/Organisatie'
import type { Projectbetrokkene } from '@models/Projectbetrokkene'

import type { Discipline } from '../../../core/util/discipline'
import { useOrganisaties } from '../../../organisaties/hooks/useOrganisaties'
import { PropertyRow } from '../../../theme/components/PropertyRow'
import { StyledError } from '../../../theme/components/StyledError'
import { useCreateProjectbetrokkene } from '../../hooks/useCreateProjectbetrokkene'
import {
  beschikbareDelegerenden,
  organisatieOptionsVoorBetrokkene,
} from '../../util/projectbetrokkene-util'

import { SelectDisciplines } from './SelectDisciplines'

export { CreateBetrokkene }

type Props = {
  /** de betrokkenen van één verantwoordelijke in één gebied */
  projectbetrokkenen: Projectbetrokkene[]
  projectType: Projectbetrokkene['projectType']
  onClose: () => void
}

const CreateBetrokkene = ({
  projectbetrokkenen,
  projectType,
  onClose,
}: Props) => {
  const { mutate: createProjectbetrokkene, error } =
    useCreateProjectbetrokkene()

  const { data: organisaties } = useOrganisaties()

  const [selectedOrganisatie, setSelectedOrganisatie] =
    useState<Organisatie | null>(null)
  const [delegerende, setDelegerende] = useState<string | null>(null)
  const [disciplines, setDisciplines] = useState<Discipline[]>([])
  const [errorText, setErrorText] = useState('')
  const [disciplinesErrorText, setDisciplinesErrorText] = useState('')
  const [delegerendeErrorText, setDelegerendeErrorText] = useState('')

  const { verantwoordelijke, gebiedscode, gebiedscodeLabel } =
    projectbetrokkenen[0]

  const selectedOrganisatieType = selectedOrganisatie?.type

  const delegerenden = selectedOrganisatie
    ? beschikbareDelegerenden(selectedOrganisatie, projectbetrokkenen)
    : []

  const handleOrganisatieChange = (value: Organisatie | null) => {
    setSelectedOrganisatie(value)
    setErrorText('')

    const mogelijkeDelegerenden = value
      ? beschikbareDelegerenden(value, projectbetrokkenen)
      : []
    setDelegerende(
      mogelijkeDelegerenden.length === 1 ? mogelijkeDelegerenden[0] : null,
    )
    setDelegerendeErrorText('')
  }

  const handleSubmit = () => {
    if (!selectedOrganisatie) {
      setErrorText('Organisatie is verplicht')

      return
    }

    if (
      selectedOrganisatieType === 'Netbeheerder' &&
      disciplines.length === 0
    ) {
      setDisciplinesErrorText('Minimaal één discipline is verplicht')

      return
    }

    if (selectedOrganisatieType === 'GedelegeerdNetbeheerder' && !delegerende) {
      setDelegerendeErrorText('Delegerende is verplicht')

      return
    }

    const projectbetrokkene: Projectbetrokkene = {
      projectType,
      verantwoordelijke,
      gebiedscode,
      gebiedscodeLabel: '',
      organisatie: selectedOrganisatie.organisatie,
      type: selectedOrganisatie.type,
    }

    if (selectedOrganisatieType === 'GedelegeerdNetbeheerder' && delegerende) {
      projectbetrokkene.delegerende = delegerende
    }

    if (selectedOrganisatieType === 'Netbeheerder') {
      projectbetrokkene.disciplines = disciplines
    }

    createProjectbetrokkene({ projectbetrokkene }, { onSuccess: onClose })
  }

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>Betrokkene toevoegen</DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <PropertyRow label="Verantwoordelijke" value={verantwoordelijke} />

        <PropertyRow label="Gebied" value={gebiedscodeLabel} />

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <SelectObject
            label="Organisatie"
            options={organisatieOptionsVoorBetrokkene(
              organisaties,
              projectbetrokkenen,
            )}
            value={selectedOrganisatie}
            onChange={handleOrganisatieChange}
            getOptionLabel={(option) => option.organisatie}
            groupBy={(option) => option.type}
            isOptionEqualToValue={(option, value) =>
              option.organisatie === value.organisatie
            }
            error={!!errorText}
            helperText={errorText}
            sx={{ flex: 1 }}
          />

          <TextField
            label="Type"
            value={selectedOrganisatie?.type ?? ''}
            variant="standard"
            size="small"
            disabled
            sx={{ flex: 1 }}
          />
        </Box>

        {selectedOrganisatieType === 'Netbeheerder' && (
          <SelectDisciplines
            projectType={projectType}
            value={disciplines}
            onChange={(value) => {
              setDisciplines(value)
              setDisciplinesErrorText('')
            }}
            errorText={disciplinesErrorText}
          />
        )}

        {selectedOrganisatieType === 'GedelegeerdNetbeheerder' && (
          <SelectString
            label="Delegerende"
            options={delegerenden}
            value={delegerende}
            onChange={(value) => {
              setDelegerende(value)
              setDelegerendeErrorText('')
            }}
            disabled={delegerenden.length === 1}
            error={!!delegerendeErrorText}
            helperText={delegerendeErrorText}
          />
        )}

        <StyledError error={error} />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} autoFocus>
          Sluiten
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          endIcon={<AddIcon />}
        >
          Toevoegen
        </Button>
      </DialogActions>
    </Dialog>
  )
}
